import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardMedia,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useApiEp from "../../../hooks/useApiEp";
import SaveIcon from "@mui/icons-material/Save";
import { getErrorResponse, getHeader, toastError } from "../../../utils/util";
import {
  Description as DescriptionIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../../../utils/constants";
import axios from "axios";
import HeaderInputs from "../../campaign/getHeaderInputs";
import BodyInputs from "../../campaign/getBodyInputs";
import TemplatePreview from "../../../widgets/TemplatePreview";
import { GlassButton, GlassButtonLoading } from "../../../themes/UiElements";
import { useDialog } from "../../../context/DialogContext";
import SectionLabel from "../../../widgets/SectionLabel";
import { toast } from "react-toastify";
import CustomCircularP from "../../../widgets/CustomCircularP";

const AddTemplate = ({
  value = {
    header: {},
    bodyParams: [],
    defaultMedia: false,
    template: null,
    mediaCards: [],
  },
  onUpdate,
}) => {
  const { data, isLoading, isError, error } = useApiEp(
    `/api/get-templates-sync`
  );
  const { showMediaDialog } = useDialog();
  const [selectedTemplate, setSelectedTemplate] = useState(value.template);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [checkingDefault, setCheckingDefault] = useState(false);
  const [defaultMedia, setDefaultMedia] = useState(value.defaultMedia);
  const [header, setHeader] = useState(value.header);
  const [bodyParams, setBodyParams] = useState(value.bodyParams);
  const [mediaCards, setMediaCards] = useState(value.mediaCards);

  const templates = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.data;
  }, [data]);

  // Load initial template if provided
  useEffect(() => {
    if (value.template && templates.length > 0 && !selectedTemplate) {
      handleTemplateLoading(value.template);
    }
  }, [value.template, templates]);

  const handleTemplateLoading = useCallback(async (template) => {
    if (!template) {
      setSelectedTemplate(null);
      return;
    }
    try {
      setTemplateLoading(true);
      const response = await axios.get(
        `${API_ENDPOINT}/api/get-template/${template.id}`,
        getHeader()
      );
      const templateD = response.data;
      const bodyComponent = templateD.components?.find(
        (c) => c.type.toLowerCase() === "body"
      );
      const exampleTextLength =
        bodyComponent?.example?.body_text?.[0]?.length ?? 0;
      console.log(new Array(exampleTextLength).fill(""));
      setBodyParams(new Array(exampleTextLength).fill(""));
      setSelectedTemplate(templateD);
    } catch (error) {
      toastError(error);
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  const handleBodyChange = (index, value) => {
    setBodyParams((prev) =>
      prev.map((param, i) => (i === index ? value : param))
    );
  };

  async function checkDefaultMedia(templateId) {
    try {
      setCheckingDefault(true);
      const response = await axios.get(
        `${API_ENDPOINT}/api/check-template-cards/${templateId}`,
        getHeader()
      );
      setMediaCards([]);
      toast.success(response.data.message);
    } catch (error) {
      setDefaultMedia(false);
      toastError(error);
    } finally {
      setCheckingDefault(false);
    }
  }

  const carouselItem = useMemo(() => {
    if (!selectedTemplate) {
      return { isCarousel: false, carouselMediaCount: 0 };
    }
    const carouselItem = selectedTemplate.components.find(
      (item) => item.type.toLowerCase() === "carousel"
    );
    const isCarousel = !!carouselItem;
    const carouselMediaCount = carouselItem?.cards?.length || 0;
    return { isCarousel, carouselMediaCount };
  }, [selectedTemplate]);

  const handleMediaCards = useCallback((cards) => {
    setMediaCards(cards);
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedTemplate) {
      toast.error("Template required");
      return;
    }
    if (selectedTemplate.status !== "APPROVED") {
      toast.error("The given template is not approved");
      return;
    }
    if (
      bodyParams.length > 0 &&
      bodyParams.some((param) => param === "" || typeof param !== "string")
    ) {
      toast.error("Body parameters must be non-empty strings");
      return;
    }
    onUpdate?.({
      template: selectedTemplate,
      bodyParams,
      header,
      defaultMedia,
      mediaCards,
      type: "template",
    });
  }, [selectedTemplate, header, bodyParams, defaultMedia, mediaCards]);

  if (isLoading) {
    return <LinearProgress />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }
  return (
    <Box>
      <CustomCircularP show={checkingDefault} />
      <Autocomplete
        size="small"
        options={templates}
        getOptionLabel={(option) => option.name}
        onChange={async (event, template) => {
          handleTemplateLoading(template);
        }}
        loading={templateLoading}
        value={selectedTemplate || null}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Template"
            variant="outlined"
            placeholder="Type template name..."
            fullWidth
            required
            error={isError}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              sx: {
                borderRadius: "8px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
              },
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: <>{params.InputProps.endAdornment}</>,
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <DescriptionIcon color="primary" />
              <Box>
                <Typography variant="subtitle1">{option.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {option.id}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
        sx={{
          "& .MuiAutocomplete-popper": {
            boxShadow: 2,
            borderRadius: "8px",
            mt: 1,
          },
        }}
        noOptionsText={"No templates available yet"}
      />
      {selectedTemplate && (
        <>
          {selectedTemplate.status !== "APPROVED" && (
            <Alert severity="error">
              This is template is {selectedTemplate.status}. Dont Use!
            </Alert>
          )}
          <Stack mt={2} direction={"column"} spacing={2}>
            {bodyParams.length > 0 && (
              <Typography variant="body2">
                If u want to use the profile name use <code>{`{name}`}</code>
              </Typography>
            )}
            <BodyInputs
              bodyParams={bodyParams}
              onBodyChange={handleBodyChange}
            />
            <HeaderInputs
              template={selectedTemplate}
              header={header}
              onHeaderChange={(newV) => setHeader(newV)}
            />
          </Stack>
          {carouselItem.isCarousel && (
            <Box sx={{ mb: 3 }}>
              <SectionLabel mt={2} color="GrayText">
                This template is carousel and it has{" "}
                {carouselItem.carouselMediaCount} media items
              </SectionLabel>
              <Stack direction="row" spacing={2} alignItems="center">
                <GlassButton
                  disabled={defaultMedia}
                  onClick={() => {
                    showMediaDialog(
                      "Select Media",
                      `Please select the media you want to use`,
                      {
                        isRcs: false,
                        mediaType: "",
                        selection: "multi",
                        selectionLimit: carouselItem.carouselMediaCount,
                        onConfirm: handleMediaCards,
                      }
                    );
                  }}
                  variant="outlined"
                  startIcon={<PermMediaIcon />}
                  sx={{ mb: 2 }}
                >
                  Select card media
                </GlassButton>

                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={defaultMedia}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            checkDefaultMedia(selectedTemplate.id);
                          }
                          setDefaultMedia(checked);
                        }}
                        color="primary"
                      />
                    }
                    label="Use default media"
                  />
                </Box>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 4 }}
              >
                Default media available for templates created on our platform.
              </Typography>

              {/* Horizontal Scrollable Media Preview */}
              {mediaCards && mediaCards.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    overflowX: "auto",
                    gap: 2,
                    pb: 2,
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.2)",
                      borderRadius: "4px",
                    },
                  }}
                >
                  {mediaCards.map((media) => (
                    <Card
                      key={media.id}
                      sx={{
                        flex: "0 0 auto",
                        width: 200,
                        height: 200,
                        borderRadius: 2,
                        boxShadow: 3,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <CardMedia
                        component={
                          media.mimeType.startsWith("image") ? "img" : "video"
                        }
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: media.mimeType.startsWith("image")
                            ? "cover"
                            : "contain",
                        }}
                        src={media.previewUrl}
                        alt={media.fileName}
                        controls={media.mimeType.startsWith("video")}
                      />
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {selectedTemplate && (
        <TemplatePreview
          template={selectedTemplate}
          previewUrl={header?.mediaUrl ?? ""}
          fileName={header?.filename ?? ""}
        />
      )}

      <Box my={2} display={"flex"} justifyContent={"end"}>
        <GlassButtonLoading
          startIcon={<SaveIcon />}
          disabled={!selectedTemplate}
          onClick={handleSave}
        >
          Save Message
        </GlassButtonLoading>
      </Box>
    </Box>
  );
};

export default AddTemplate;
